/**
 * Session-only cache of decrypted distributed-search files (TASK-UI-VFS-004-3).
 *
 * Plaintext lives ONLY here: a `Map<nodeId, Blob>` plus the `blob:` URL handed to viewers.
 * Nothing is written to localStorage / sessionStorage / IndexedDB. Every URL is revoked
 * on logout (authStore.logout -> `clear()`), on non-bfcache `pagehide`, and when an entry
 * is replaced or removed.
 */
export interface SessionBlobCache {
  /** Stores (replacing + revoking any previous entry) and returns the object URL. */
  put(nodeId: string, blob: Blob): string;
  getBlob(nodeId: string): Blob | undefined;
  getUrl(nodeId: string): string | undefined;
  has(nodeId: string): boolean;
  remove(nodeId: string): void;
  /** Revoke every URL and drop every Blob. */
  clear(): void;
  readonly size: number;
}

export function createSessionBlobCache(): SessionBlobCache {
  const blobs = new Map<string, Blob>();
  const urls = new Map<string, string>();
  let pagehideInstalled = false;

  const revoke = (nodeId: string) => {
    const u = urls.get(nodeId);
    if (u) URL.revokeObjectURL(u);
    urls.delete(nodeId);
    blobs.delete(nodeId);
  };

  const installPagehide = () => {
    if (pagehideInstalled || typeof window === "undefined") return;
    pagehideInstalled = true;
    window.addEventListener("pagehide", (e: PageTransitionEvent) => {
      if (e.persisted) return; // bfcache: the page may be restored with its state
      cache.clear();
    });
  };

  const cache: SessionBlobCache = {
    put(nodeId, blob) {
      installPagehide();
      revoke(nodeId);
      const url = URL.createObjectURL(blob);
      blobs.set(nodeId, blob);
      urls.set(nodeId, url);
      return url;
    },
    getBlob: (nodeId) => blobs.get(nodeId),
    getUrl: (nodeId) => urls.get(nodeId),
    has: (nodeId) => blobs.has(nodeId),
    remove: revoke,
    clear() {
      for (const id of [...urls.keys()]) revoke(id);
      blobs.clear();
    },
    get size() {
      return blobs.size;
    },
  };
  return cache;
}

export const sessionBlobCache = createSessionBlobCache();
